export interface MenuItem {
  /** Guest-facing dish code (e.g. "12", "N1") for ordering without the dish name. */
  code?: string;
  name: string;
  price: string;
  description: string;
  /** Optional dish photo URL (Dish Cards / gallery variants). */
  imageUrl?: string;
  allergenIds?: string[];
  additiveIds?: string[];
  dietaryTagIds?: string[];
}

export interface MenuColumn {
  title: string;
  /** Optional category photo URL (Category Gallery). */
  imageUrl?: string;
  items: MenuItem[];
}

/** Parses static grid or tabbed menu JSON (array or `{ columns }` wrapper). */
export function parseMenuColumns(raw: string): MenuColumn[] {
  if (!raw.trim()) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter(isMenuColumn).map(withMenuItems);
    }
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'columns' in parsed &&
      Array.isArray((parsed as { columns: unknown }).columns)
    ) {
      return (parsed as { columns: unknown[] }).columns.filter(isMenuColumn).map(withMenuItems);
    }
    return [];
  } catch {
    return [];
  }
}

function isMenuColumn(value: unknown): value is MenuColumn {
  return (
    typeof value === 'object' &&
    value !== null &&
    'title' in value &&
    typeof (value as MenuColumn).title === 'string' &&
    Array.isArray((value as MenuColumn).items)
  );
}

function withMenuItems(column: MenuColumn): MenuColumn {
  return {
    ...column,
    imageUrl: typeof column.imageUrl === 'string' ? column.imageUrl : undefined,
    items: column.items.map(asMenuItem),
  };
}

function asMenuItem(value: unknown): MenuItem {
  const row = (value ?? {}) as Record<string, unknown>;
  return {
    code: typeof row.code === 'string' ? row.code : undefined,
    name: typeof row.name === 'string' ? row.name : '',
    price: typeof row.price === 'string' ? row.price : '',
    description: typeof row.description === 'string' ? row.description : '',
    imageUrl: typeof row.imageUrl === 'string' ? row.imageUrl : undefined,
    allergenIds: Array.isArray(row.allergenIds) ? (row.allergenIds as string[]) : undefined,
    additiveIds: Array.isArray(row.additiveIds) ? (row.additiveIds as string[]) : undefined,
    dietaryTagIds: Array.isArray(row.dietaryTagIds) ? (row.dietaryTagIds as string[]) : undefined,
  };
}
